import React, { useEffect, useRef, useState } from 'react';
import { GripHorizontal, Eye, EyeClosed, ChevronRight, Plus, Settings2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Tag } from '@getro/rombo';

export const Fields = ({
  tableRef,
  selectedColumns,
  setSelectedColumns,
  hideCreateCustomFields,
  onCreateCustomField,
  onEditCustomField,
}) => {
  const [localColumns, setLocalColumns] = useState(selectedColumns);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const visibleColumns = localColumns.filter((column) => !column.hide);

  useEffect(() => {
    setLocalColumns(selectedColumns);
  }, [selectedColumns]);

  const dragItem = useRef();
  const dragOverItem = useRef();

  useEffect(() => {
    const handleDragEnd = () => {
      if (dragItem.current === null || dragOverItem.current === null) return;

      const fromIndex = dragItem.current;
      const toIndex = dragOverItem.current;

      // Update state - AG Grid will re-render with new column order
      const newColumns = [...selectedColumns];
      const draggedItemContent = newColumns.splice(fromIndex, 1)[0];
      // Adjust toIndex when moving to a later position since array shifted after removal
      const adjustedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
      newColumns.splice(adjustedToIndex, 0, draggedItemContent);

      dragItem.current = null;
      dragOverItem.current = null;

      setLocalColumns(newColumns);
      setSelectedColumns(newColumns, 'reorder', draggedItemContent);

      setDraggedItem(null);
      setDragOverIndex(null);
    };

    document.addEventListener('dragend', handleDragEnd);
    return () => document.removeEventListener('dragend', handleDragEnd);
  }, [selectedColumns, setSelectedColumns, tableRef]);

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItem(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    // Skip if dragging over the same item
    if (index === draggedItem) return;

    // Get the mouse position relative to the element
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const elementTop = rect.top;
    const elementHeight = rect.height;
    const relativeY = mouseY - elementTop;

    // Determine if we're in the top or bottom half of the element
    const isTopHalf = relativeY < elementHeight / 2;

    // Set drop position based on mouse position and current item
    if (isTopHalf) {
      dragOverItem.current = index;
      setDragOverIndex(index);
    } else {
      dragOverItem.current = index + 1;
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear drag over index if we're leaving the entire dropdown area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  return (
    <Box
      sx={{
        pt: '8px',
        width: '280px',
        maxHeight: '680px',
        overflowY: 'auto',
        boxShadow: '0px 2px 32px 0px #1B24320F, 0px 1px 4px 0px #1B24320F',
        position: 'absolute',
        left: ['-100px', '-100px', '0'],
        top: '30px',
        zIndex: 11000,
        backgroundColor: 'white',
      }}
      onDragLeave={handleDragLeave}
    >
      {visibleColumns.map((column, index) => (
        <Box key={column.accessor} sx={{ position: 'relative' }}>
          <Box
            sx={{
              display: column.hideOnTableColumn ? 'none' : 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              opacity: draggedItem === index ? 0.5 : 1,
              transform: draggedItem === index ? 'scale(1.02)' : 'scale(1)',
              transition: 'opacity 0.2s, transform 0.2s, background-color 0.2s',
              p: '6px 8px',
              bg: draggedItem === index ? 'primary.50' : 'neutral.0',
              color: 'text.dark',
              '&:hover': {
                bg: draggedItem === index ? 'primary.100' : 'neutral.20',
              },
              cursor: column.suppressMovable ? 'not-allowed' : 'inherit',
            }}
            data-accessor={column.accessor}
            draggable={!column.suppressMovable}
            onDragStart={(e) => !column.suppressMovable && handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
          >
            <Flex sx={{ alignItems: 'center', gap: '8px' }}>
              <Box
                flex="0 0 auto"
                sx={{
                  color: draggedItem === index ? 'primary.main' : 'neutral.400',
                  cursor: column.suppressMovable ? 'not-allowed' : 'grab',
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                size="16px"
                as={GripHorizontal}
              />
              <Text
                sx={{
                  fontSize: '14px',
                  fontWeight: draggedItem === index ? 500 : 400,
                  color: column.show ? 'text.main' : 'text.placeholder',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {column.Header}
              </Text>
              {column.customField && (
                <Tag
                  sx={{
                    flex: '0 0 auto',
                    pl: '8px',
                    pr: '8px!important',
                    py: '4px',
                    mr: 0,
                    '&& > div': { fontSize: '10px', fontWeight: '500', textTransform: 'uppercase' },
                  }}
                >
                  {column.customField.sharedList?.id ? 'List-specific' : 'Global'}
                </Tag>
              )}
            </Flex>
            <Flex flex="0 0 auto" sx={{ alignItems: 'center', gap: '8px', ml: '8px' }}>
              {column.customField && !hideCreateCustomFields && (
                <Flex
                  alignItems="center"
                  onClick={(event) => onEditCustomField(event, column.customField)}
                  sx={{ cursor: 'pointer', color: 'neutral.500', '&:hover': { color: 'neutral.700' } }}
                >
                  <Box as={Settings2} width="16px" height="16px" aria-hidden="true" />
                </Flex>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (column.suppressMovable) return;

                  const newColumns = localColumns.map((item) => {
                    if (item.accessor === column.accessor) {
                      return { ...item, show: !item.show };
                    }
                    return item;
                  });

                  setLocalColumns(newColumns);
                  setSelectedColumns(newColumns, !column.show ? 'show' : 'hide', column);
                }}
              >
                <Box
                  as={column.show ? Eye : EyeClosed}
                  size="16px"
                  sx={{
                    color: column.show ? 'text.dark' : 'neutral.400',
                    cursor: column.suppressMovable ? 'default' : 'pointer',
                  }}
                />
              </Box>
            </Flex>
          </Box>

          {draggedItem !== null && dragOverIndex === index && draggedItem !== index && (
            <Box
              sx={{
                height: '2px',
                backgroundColor: 'purple.300',
                margin: '0 8px',
                borderRadius: '1px',
                transition: 'opacity 0.2s',
                position: 'absolute',
                top: '-1px',
                left: '-8px',
                right: '8px',
                width: '100%',
              }}
            />
          )}
        </Box>
      ))}
      {!hideCreateCustomFields && (
        <Flex width="100%" flexDirection="column" sx={{ position: 'sticky', bottom: 0, bg: 'white', pb: '4px' }}>
          <Box width="100%" my="4px" sx={{ height: '1px', backgroundColor: 'border.subtle' }} />
          <Flex
            p="8px"
            alignItems="center"
            justifyContent="space-between"
            flex="1"
            sx={{ cursor: 'pointer', '&:hover': { bg: 'neutral.20' } }}
            onClick={onCreateCustomField}
          >
            <Flex alignItems="center" sx={{ gap: '8px' }}>
              <Box as={Plus} width="16px" height="16px" aria-hidden="true" />
              <Text fontSize="14px" fontWeight="medium">
                Create custom field
              </Text>
            </Flex>
            <Box as={ChevronRight} width="16px" height="16px" aria-hidden="true" />
          </Flex>
        </Flex>
      )}
    </Box>
  );
};

Fields.propTypes = {
  tableRef: PropTypes.object.isRequired,
  selectedColumns: PropTypes.array.isRequired,
  setSelectedColumns: PropTypes.func.isRequired,
  hideCreateCustomFields: PropTypes.bool,
  onCreateCustomField: PropTypes.func.isRequired,
  onEditCustomField: PropTypes.func.isRequired,
};

Fields.defaultProps = {
  hideCreateCustomFields: false,
};
